/**
 * 📄 MODAL ENREGISTREMENT PAIEMENT FOURNISSEUR
 * ==============================================
 * Modal pour enregistrer un règlement fournisseur (décaissement)
 *
 * Fonctionnalités:
 * - Saisie montant (validé contre balance_due)
 * - Sélection mode de paiement
 * - Date et référence de paiement
 * - Calcul automatique nouveau solde
 * - Mise à jour automatique statut facture (via trigger DB)
 *
 * Workflow:
 * 1. Utilisateur ouvre modal depuis SupplierBillDetailModal
 * 2. Saisit montant <= balance_due
 * 3. Sélectionne mode paiement (optionnel)
 * 4. Clique "Enregistrer" → Backend + Trigger MAJ auto
 * 5. Modal se ferme → Callback onSuccess pour refresh
 *
 * @see src/gestion-logistics/hooks/useSupplierPayments.ts
 * @see routes/logistics/supplier-payments.js
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Alert,
  Space,
  Typography,
  Divider,
  Card,
  Row,
  Col,
  Statistic,
  message,
} from 'antd';
import {
  DollarOutlined,
  CalendarOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import apiClient from '../../api/apiClient';
import dayjs, { Dayjs } from 'dayjs';
import API_CONFIG from '../../config/api';
import { useSelection } from '../../SelectionContext';
import { useTreasuryAccountSuggestion } from '../../hooks/useCommercialTreasurySuggestion';
import useSupplierPayments from '../hooks/useSupplierPayments';
import { SupplierBill } from '../types/invoice.types';
import { TreasuryAccount } from '../types/treasury.types';

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

interface SupplierPaymentRecordModalProps {
  visible: boolean;
  onClose: () => void;
  supplierBill: SupplierBill | null;
  onSuccess?: () => void;
}

const SupplierPaymentRecordModal: React.FC<SupplierPaymentRecordModalProps> = ({
  visible,
  onClose,
  supplierBill,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const { recordPayment, loading } = useSupplierPayments();
  const { selectedCompany, selectedWarehouse } = useSelection();

  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [treasuryAccounts, setTreasuryAccounts] = useState<TreasuryAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  const [paymentModes, setPaymentModes] = useState<Array<{ id: number; name: string }>>([]);
  const { onModeChange: suggestAccount, onAccountChange: selectAccount } = useTreasuryAccountSuggestion(form, {
    warehouseId: selectedWarehouse, companyId: selectedCompany, moduleScope: 'logistics',
    enabled: visible && !!supplierBill, accounts: treasuryAccounts,
    modeField: 'payment_mode_id', accountField: 'treasury_account_id',
  });

  // Calculer le nouveau solde après paiement
  const newBalance = useMemo(() => {
    if (!supplierBill) return 0;
    const currentBalance = supplierBill.balance_due || 0;
    return Math.max(0, currentBalance - paymentAmount);
  }, [supplierBill, paymentAmount]);

  // Nouveau statut prévu après paiement
  const newStatus = useMemo(() => {
    if (!supplierBill) return '';
    if (newBalance === 0) return 'paid';
    if (newBalance < (supplierBill.balance_due || 0)) return 'partial';
    return supplierBill.payment_status || 'unpaid';
  }, [supplierBill, newBalance]);

  // Fonction utilitaire pour formater les montants
  const formatAmount = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined || isNaN(amount)) return '0';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(amount));
  };

  const formatCurrency = (amount: number | null | undefined): string => {
    const formatted = formatAmount(amount);
    return `${formatted} XOF`;
  };

  // Réinitialiser le formulaire à l'ouverture
  useEffect(() => {
    if (visible && supplierBill) {
      form.resetFields();
      setPaymentAmount(0);
      form.setFieldsValue({
        payment_date: dayjs(),
        amount: supplierBill.balance_due, // Suggérer le solde complet par défaut
      });
      setPaymentAmount(supplierBill.balance_due || 0);
    }
  }, [visible, supplierBill, form]);

  useEffect(() => {
    if (!visible) return undefined;
    const controller = new AbortController();
    setTreasuryAccounts([]);
    setPaymentModes([]);
    form.setFieldsValue({ treasury_account_id: undefined, payment_mode_id: undefined });
    const fetchAccounts = async () => {
      if (!selectedCompany || !selectedWarehouse) {
        setAccountsLoading(false);
        return;
      }

      setAccountsLoading(true);
      try {
        const response = await apiClient.get(
          `${API_CONFIG.API_URL}/logistics/payments/options`,
          {
            signal: controller.signal,
            params: {
              company_id: selectedCompany,
              warehouse_id: selectedWarehouse,
            },
          }
        );

        if (!controller.signal.aborted && response.data?.success) {
          const accounts = (response.data.accounts || []).filter((account: TreasuryAccount) =>
            !supplierBill?.currency_id || Number(account.currency_id) === Number(supplierBill.currency_id));
          setTreasuryAccounts(accounts);
          setPaymentModes(response.data.modes || []);

        }
      } catch (error) {
        if (!controller.signal.aborted) message.error('Les options de paiement ne sont pas disponibles. Rouvrez le formulaire pour réessayer.');
      } finally {
        if (!controller.signal.aborted) setAccountsLoading(false);
      }
    };

    fetchAccounts();
    return () => controller.abort();
  }, [form, selectedCompany, selectedWarehouse, visible, supplierBill?.currency_id]);

  // Handler changement montant
  const handleAmountChange = (value: number | null) => {
    setPaymentAmount(value || 0);
  };

  // Soumettre le formulaire
  const handleSubmit = async () => {
    try {
      if (!supplierBill) {
        message.error('Facture fournisseur non spécifiée');
        return;
      }

      const values = await form.validateFields();

      // Validation montant
      if (values.amount <= 0) {
        message.warning('Le montant doit être positif');
        return;
      }

      if (values.amount > (supplierBill.balance_due || 0)) {
        message.warning('Le montant dépasse le solde dû');
        return;
      }

      console.log('💰 [SupplierPaymentRecordModal] Enregistrement paiement:', {
        supplier_bill_id: supplierBill.id,
        amount: values.amount,
        balance_before: supplierBill.balance_due,
        balance_after: newBalance,
      });

      // Récupérer le nom du mode de paiement
      let paymentModeName = undefined;
      if (values.payment_mode_id) {
        const selectedMode = paymentModes.find((m) => m.id === values.payment_mode_id);
        paymentModeName = selectedMode?.name;
      }

      // Préparer les données pour l'API
      const paymentData = {
        supplier_bill_id: supplierBill.id,
        payment_date: values.payment_date ? values.payment_date.format('YYYY-MM-DD') : undefined,
        amount: values.amount,
        payment_reference: values.payment_reference || undefined,
        payment_mode_id: values.payment_mode_id || undefined,
        payment_mode_name: paymentModeName,
        treasury_account_id: values.treasury_account_id,
        currency_id: supplierBill.currency_id || undefined,
        notes: values.notes || undefined,
      };

      // Appeler le hook
      const result = await recordPayment(paymentData);

      if (result) {
        message.success('Règlement enregistré avec succès !');
        onClose();
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      console.error('❌ [SupplierPaymentRecordModal] Erreur validation:', error);
      message.error('Erreur lors de l\'enregistrement du règlement');
    }
  };

  return (
    <Modal
      title={
        <Space>
          <DollarOutlined />
          <span>Enregistrer un Règlement Fournisseur</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={700}
      okText="Enregistrer le règlement"
      cancelText="Annuler"
      onOk={handleSubmit}
      confirmLoading={loading}
      okButtonProps={{ disabled: accountsLoading || treasuryAccounts.length === 0 }}
      destroyOnClose
    >
      {/* Info facture */}
      {supplierBill && (
        <Alert
          message={`Facture ${supplierBill.bill_number} - ${supplierBill.supplier_name}`}
          description={
            <div>
              <Text>
                Montant total : <strong>{formatCurrency(supplierBill.total_amount)}</strong>
              </Text>
              <br />
              <Text type="danger">
                Solde dû : <strong>{formatCurrency(supplierBill.balance_due)}</strong>
              </Text>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Formulaire */}
      <Form form={form} layout="vertical">
        {treasuryAccounts.length === 0 && (
          <Alert
            message="Aucun compte de tresorerie"
            description="Creez un compte de tresorerie pour enregistrer ce reglement."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Montant du règlement"
              name="amount"
              rules={[
                { required: true, message: 'Montant requis' },
                {
                  validator: (_, value) => {
                    if (!value || value <= 0) {
                      return Promise.reject('Le montant doit être positif');
                    }
                    if (value > (supplierBill?.balance_due || 0)) {
                      return Promise.reject(
                        `Ne peut pas dépasser le solde dû (${formatCurrency(supplierBill?.balance_due)})`
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              tooltip="Montant à payer (ne peut pas dépasser le solde dû)"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={supplierBill?.balance_due || 0}
                precision={0}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                parser={(value) => Number(value!.replace(/\s/g, ''))}
                placeholder="Montant"
                prefix={<DollarOutlined />}
                suffix="XOF"
                onChange={handleAmountChange}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Date du règlement"
              name="payment_date"
              rules={[{ required: true, message: 'Date requise' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder="Sélectionner la date"
                prefix={<CalendarOutlined />}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Compte de tresorerie"
          name="treasury_account_id"
          rules={[{ required: true, message: 'Compte requis' }]}
        >
          <Select
            placeholder="Selectionner un compte"
            loading={accountsLoading}
            disabled={treasuryAccounts.length === 0}
            onChange={selectAccount}
          >
            {treasuryAccounts.map((account) => (
              <Option key={account.id} value={account.id}>
                {account.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Référence de paiement" name="payment_reference">
              <Input
                placeholder="Ex: CHQ-12345, VIR-98765"
                prefix={<FileTextOutlined />}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="Mode de paiement" name="payment_mode_id">
              <Select placeholder="Sélectionner un mode" allowClear loading={accountsLoading} onChange={suggestAccount}>
                {paymentModes.map((mode) => (
                  <Option key={mode.id} value={mode.id}>
                    {mode.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Notes */}
        <Form.Item label="Notes" name="notes">
          <TextArea
            rows={2}
            placeholder="Notes ou remarques pour ce règlement..."
            maxLength={300}
            showCount
          />
        </Form.Item>
      </Form>

      <Divider>Aperçu du nouveau solde</Divider>

      {/* Aperçu du nouveau solde */}
      <Row gutter={[16, 16]}>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Solde actuel"
              value={supplierBill?.balance_due || 0}
              formatter={(value) => formatCurrency(value as number)}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>

        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Montant à payer"
              value={paymentAmount}
              formatter={(value) => formatCurrency(value as number)}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>

        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Nouveau solde"
              value={newBalance}
              formatter={(value) => formatCurrency(value as number)}
              prefix={<CheckCircleOutlined />}
              valueStyle={{
                color: newBalance === 0 ? '#52c41a' : '#faad14',
                fontWeight: 'bold',
              }}
            />
            <div style={{ marginTop: 8, textAlign: 'center' }}>
              {newStatus === 'paid' && (
                <Text type="success" style={{ fontSize: '12px' }}>
                  ✅ Facture sera marquée comme PAYÉE
                </Text>
              )}
              {newStatus === 'partial' && (
                <Text type="warning" style={{ fontSize: '12px' }}>
                  ⚠️ Paiement PARTIEL
                </Text>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </Modal>
  );
};

export default SupplierPaymentRecordModal;
